import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./OffseasonTimeline.css";
import API_URL from "./config";

const OffseasonTimeline = ({ onAdvanceToRegularSeason, isAdvancing }) => {
  const navigate = useNavigate();
  const [completedSteps, setCompletedSteps] = useState({
    progression: false,
    draft: false,
    freeAgency: false,
  });
  const [progressionLoading, setProgressionLoading] = useState(false);
  const [progressionAlreadyDone, setProgressionAlreadyDone] = useState(null);
  const [statusChecked, setStatusChecked] = useState(false);
  const [currentPhase, setCurrentPhase] = useState(0); // 0=progression, 1=draft, 2=freeAgency, 3=regularSeason

  const phases = [
    { id: "progression", label: "Player Progression" },
    { id: "draft", label: "Draft" },
    { id: "freeAgency", label: "Free Agency" },
    { id: "regularSeason", label: "Advance Season" },
  ];

  // Initialize current phase from localStorage
  useEffect(() => {
    const activeSaveId = localStorage.getItem("activeSaveId");
    if (activeSaveId) {
      const savedPhase = localStorage.getItem(`current_phase_${activeSaveId}`);
      if (savedPhase) {
        const phase = parseInt(savedPhase, 10);
        // Ensure phase is within valid bounds
        if (phase >= 0 && phase < phases.length) {
          setCurrentPhase(phase);
        }
      }
      
      // Initialize completedSteps from localStorage
      const progressionCompleted = localStorage.getItem(`progression_completed_${activeSaveId}`) === "true";
      const draftCompleted = localStorage.getItem(`draft_completed_${activeSaveId}`) === "true";
      const freeAgencyCompleted = localStorage.getItem(`free_agency_completed_${activeSaveId}`) === "true";
      
      if (progressionCompleted || draftCompleted || freeAgencyCompleted) {
        setCompletedSteps({
          progression: progressionCompleted,
          draft: draftCompleted,
          freeAgency: freeAgencyCompleted,
        });
      }
    }
  }, []);

  // Move to next phase
  const advancePhase = () => {
    const activeSaveId = localStorage.getItem("activeSaveId");
    if (activeSaveId && currentPhase < phases.length - 1) {
      const nextPhase = currentPhase + 1;
      setCurrentPhase(nextPhase);
      localStorage.setItem(`current_phase_${activeSaveId}`, nextPhase.toString());
    }
  };

  // Check if player progression has already been completed this offseason
  useEffect(() => {
    const checkProgressionStatus = async () => {
      try {
        const activeSaveId = localStorage.getItem("activeSaveId");
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const userId = user._id;

        if (!activeSaveId) {
          setProgressionAlreadyDone(false);
          setStatusChecked(true);
          return;
        }

        // Check localStorage FIRST for immediate UX feedback
        const localStorageKey = `progression_completed_${activeSaveId}`;
        const localStorageValue = localStorage.getItem(localStorageKey);
        
        if (localStorageValue === "true") {
          setProgressionAlreadyDone(true);
          setCompletedSteps((prev) => ({ ...prev, progression: true }));
          setStatusChecked(true);
          // Still verify with database in background
          try {
            const dbResponse = await fetch(`${API_URL}/api/saves/${activeSaveId}`, {
              headers: { "X-User-ID": userId },
            });
            if (dbResponse.ok) {
              const data = await dbResponse.json();
              const save = data.save;
              const isCompleted = save && save.player_progression_completed === true;
              if (!isCompleted) {
                // Database says it's not done, clear localStorage
                localStorage.removeItem(localStorageKey);
                setProgressionAlreadyDone(false);
              }
            }
          } catch (err) {
            // Ignore database errors, localStorage is already set
          }
          return;
        }

        // If localStorage doesn't have it, check database
        const response = await fetch(`${API_URL}/api/saves/${activeSaveId}`, {
          headers: { "X-User-ID": userId },
        });

        if (response.ok) {
          const data = await response.json();
          const save = data.save;
          const isCompleted = save && save.player_progression_completed === true;
          setProgressionAlreadyDone(isCompleted);
          if (isCompleted) {
            localStorage.setItem(localStorageKey, "true");
            setCompletedSteps((prev) => ({ ...prev, progression: true }));
          }
        } else {
          setProgressionAlreadyDone(false);
        }
      } catch (err) {
        // Error checking progression status
        setProgressionAlreadyDone(false);
      } finally {
        setStatusChecked(true);
      }
    };

    checkProgressionStatus();
  }, []);

  const handlePlayerProgression = async () => {
    if (progressionAlreadyDone) {
      alert("Player progression has already been completed this offseason.");
      return;
    }

    setProgressionLoading(true);
    const activeSaveId = localStorage.getItem("activeSaveId");
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user._id;

      if (!activeSaveId) {
        alert("No active save file");
        setProgressionLoading(false);
        return;
      }

      const response = await fetch(
        `${API_URL}/api/saves/${activeSaveId}/player-progression`,
        {
          method: "POST",
          headers: { "X-User-ID": userId, "Content-Type": "application/json" },
        }
      );

      if (!response.ok) {
        // Parse error response from backend
        try {
          const errorData = await response.json();
          const errorMsg = errorData.detail || "Failed to process player progression";
          
          if (errorMsg.includes("already been completed")) {
            const localStorageKey = `progression_completed_${activeSaveId}`;
            localStorage.setItem(localStorageKey, "true");
            
            setProgressionAlreadyDone(true);
            setCompletedSteps({ ...completedSteps, progression: true });
            alert("Player progression has already been completed this offseason.");
            setProgressionLoading(false);
            return;
          }
          throw new Error(errorMsg);
        } catch (e) {
          throw new Error(e.message || "Failed to process player progression");
        }
      }

      const result = await response.json();
      
      // Mark as completed - store in localStorage immediately for instant UX feedback
      const localStorageKey = `progression_completed_${activeSaveId}`;
      localStorage.setItem(localStorageKey, "true");
      
      setProgressionAlreadyDone(true);
      setCompletedSteps({ ...completedSteps, progression: true });
      
      // Navigate to results page - DO NOT auto-advance phase yet
      // User must click "Finalize Progression" button to advance
      navigate("/player-progression-results", {
        state: { results: result },
      });
    } catch (err) {
      const errorMsg = err.message || "Unknown error";
      if (errorMsg.includes("already been completed")) {
        const localStorageKey = `progression_completed_${activeSaveId}`;
        localStorage.setItem(localStorageKey, "true");
        
        setProgressionAlreadyDone(true);
        alert("Player progression has already been completed this offseason.");
      } else {
        alert("Error processing player progression: " + errorMsg);
      }
    } finally {
      setProgressionLoading(false);
    }
  };

  const handleFinalizeProgression = () => {
    // Mark progression as finalized and advance to draft phase
    const nextPhase = currentPhase + 1;
    setCurrentPhase(nextPhase);
    const activeSaveId = localStorage.getItem("activeSaveId");
    if (activeSaveId) {
      localStorage.setItem(`current_phase_${activeSaveId}`, nextPhase.toString());
    }
  };

  const handleDraft = () => {
    // Navigate to draft - don't mark as completed yet
    navigate("/draft");
  };

  const handleFinalizeDraft = () => {
    // Mark draft as completed and advance to free agency phase
    setCompletedSteps({ ...completedSteps, draft: true });
    const activeSaveId = localStorage.getItem("activeSaveId");
    if (activeSaveId) {
      localStorage.setItem(`draft_completed_${activeSaveId}`, "true");
    }
    const nextPhase = currentPhase + 1;
    setCurrentPhase(nextPhase);
    if (activeSaveId) {
      localStorage.setItem(`current_phase_${activeSaveId}`, nextPhase.toString());
    }
  };
  
  // Check draft completion status periodically
  useEffect(() => {
    const checkDraftStatus = async () => {
      try {
        const activeSaveId = localStorage.getItem("activeSaveId");
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const userId = user._id;

        if (!activeSaveId) return;

        const response = await fetch(`${API_URL}/api/saves/${activeSaveId}`, {
          headers: { "X-User-ID": userId },
        });

        if (response.ok) {
          const data = await response.json();
          const save = data.save;
          const draftStatus = save?.draftStatus || "not_started";
          
          // Only mark draft as completed in UI - do NOT auto-advance
          if (draftStatus === "completed" && !completedSteps.draft) {
            setCompletedSteps((prev) => ({ ...prev, draft: true }));
          }
        }
      } catch (err) {
        // Error checking draft status
      }
    };

    // Only check if we're actively on draft phase or if draft is in progress
    if (currentPhase >= 1) {
      const interval = setInterval(checkDraftStatus, 1000); // Check every second
      checkDraftStatus(); // Check immediately
      return () => clearInterval(interval);
    }
  }, [currentPhase, completedSteps.draft]);

  // Check free agency completion status when component mounts or currentPhase changes
  useEffect(() => {
    const checkFreeAgencyStatus = async () => {
      try {
        const activeSaveId = localStorage.getItem("activeSaveId");
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const userId = user._id;

        if (!activeSaveId) return;

        const response = await fetch(`${API_URL}/api/saves/${activeSaveId}`, {
          headers: { "X-User-ID": userId },
        });

        if (response.ok) {
          const data = await response.json();
          const save = data.save;
          const isFreeAgencyCompleted = save?.freAgencyBiddingCompleted === true;
          
          // If free agency is completed in database but not marked in state, update it
          // But do NOT auto-advance - user must click finalize button
          if (isFreeAgencyCompleted && !completedSteps.freeAgency) {
            setCompletedSteps((prev) => ({ ...prev, freeAgency: true }));
          }
        }
      } catch (err) {
        // Error checking free agency status
      }
    };

    checkFreeAgencyStatus();
  }, []); // Only run on mount

  const handleFreeAgency = () => {
    // Navigate to dedicated free agency page
    navigate("/free-agency");
  };

  const handleSigningComplete = () => {
    // Mark as completed and advance to regular season
    setCompletedSteps({ ...completedSteps, freeAgency: true });
    advancePhase();
  };

  const handleAdvanceRegularSeason = () => {
    onAdvanceToRegularSeason();
  };

  const timelineSteps = [
    {
      id: "progression",
      label: "Player Progression",
      description: "Develop your roster",
      icon: "📈",
      onClick: handlePlayerProgression,
      completed: completedSteps.progression,
      isLoading: progressionAlreadyDone === null || progressionLoading,
      disabled: progressionAlreadyDone === true,
    },
    {
      id: "draft",
      label: "Draft",
      description: "Build with prospects",
      icon: "🎯",
      onClick: handleDraft,
      completed: completedSteps.draft,
    },
    {
      id: "freeAgency",
      label: "Free Agency",
      description: "Sign players",
      icon: "🤝",
      onClick: handleFreeAgency,
      completed: completedSteps.freeAgency,
    },
    {
      id: "regularSeason",
      label: "Advance Season",
      description: "Start regular season",
      icon: "🏀",
      onClick: handleAdvanceRegularSeason,
      isLoading: isAdvancing,
      isPrimary: true,
    },
  ];

  // Check if progression is complete - synchronous check from localStorage
  const isProgressionComplete = () => {
    const activeSaveId = localStorage.getItem("activeSaveId");
    const localStorageKey = `progression_completed_${activeSaveId}`;
    return localStorage.getItem(localStorageKey) === "true";
  };

  return (
    <div className="offseason-timeline-container">
      <div className="timeline-header">
        <h2 className="timeline-title">Prepare for Next Season</h2>
        <p className="timeline-subtitle">Complete offseason activities in order</p>
      </div>

      <div className="timeline-track">
        {timelineSteps.map((step, index) => {
          // Only show the current phase - hide all others
          if (index !== currentPhase) {
            return null;
          }
          
          return (
            <div key={step.id} className="timeline-step-wrapper">
              <div className="timeline-step">
                {step.id === "progression" && completedSteps.progression ? (
                  <div className="progression-complete-text">
                    <p>✓ Player Progression Complete</p>
                    <button 
                      className="timeline-button primary"
                      onClick={handleFinalizeProgression}
                    >
                      <span className="step-icon">➜</span>
                      <span className="step-label">Proceed to Draft</span>
                    </button>
                  </div>
                ) : step.id === "draft" && completedSteps.draft ? (
                  <div className="draft-complete-text">
                    <p>✓ Draft Complete</p>
                    <button 
                      className="timeline-button primary"
                      onClick={handleFinalizeDraft}
                    >
                      <span className="step-icon">➜</span>
                      <span className="step-label">Proceed to Free Agency</span>
                    </button>
                  </div>
                ) : step.id === "freeAgency" && completedSteps.freeAgency ? (
                  <div className="free-agency-complete-text">
                    <p>✓ Free Agency Complete</p>
                    <button 
                      className="timeline-button primary"
                      onClick={handleSigningComplete}
                    >
                      <span className="step-icon">➜</span>
                      <span className="step-label">Advance to Regular Season</span>
                    </button>
                  </div>
                ) : (
                  <button
                    className={`timeline-button ${step.completed ? "completed" : ""} ${step.isPrimary ? "primary" : ""}`}
                    onClick={step.onClick}
                    disabled={step.isLoading}
                    title={step.description}
                  >
                    <span className="step-icon">{step.icon}</span>
                    <span className="step-label">
                      {step.isLoading ? "Processing..." : step.label}
                    </span>
                  </button>
                )}
                <p className="step-description">{step.description}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OffseasonTimeline;
